/**
 * Arabic-first formatting for the public reading surfaces.
 *
 * House rule: numerals are ALWAYS Latin (0-9), everywhere in the app, even on
 * fully Arabic pages. Arabic-Indic digits (٠١٢…) and Persian digits (۰۱۲…) are
 * never rendered — `toLatinDigits` normalises any that arrive in stored content.
 *
 * Month names stay Levantine (آب، أيلول), not Egyptian (أغسطس، سبتمبر):
 * `toLocaleDateString("ar")` and `"ar-u-nu-latn"` both give the Egyptian set, so we use
 * an explicit table instead of a locale string.
 */

const ARABIC_INDIC_RANGE = /[\u0660-\u0669\u06F0-\u06F9]/g;

/** Convert every Arabic-Indic / Persian digit in a value to a Latin digit. */
export function toLatinDigits<T>(value: T): T;
export function toLatinDigits(value: unknown): unknown {
  if (typeof value === "string") {
    return value.replace(ARABIC_INDIC_RANGE, (d) =>
      String(d.charCodeAt(0) >= 0x06f0 ? d.charCodeAt(0) - 0x06f0 : d.charCodeAt(0) - 0x0660),
    );
  }
  if (Array.isArray(value)) return value.map((v) => toLatinDigits(v));
  if (value && typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      out[k] = toLatinDigits(v);
    }
    return out;
  }
  return value;
}

/** Levantine (Bilad al-Sham) month names, January → December. */
const LEVANT_MONTHS = [
  "كانون الثاني",
  "شباط",
  "آذار",
  "نيسان",
  "أيار",
  "حزيران",
  "تموز",
  "آب",
  "أيلول",
  "تشرين الأول",
  "تشرين الثاني",
  "كانون الأول",
];

/** «19 آب 2026» */
export function arabicDate(value?: string | Date | null): string {
  if (!value) return "";
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getDate()} ${LEVANT_MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

/**
 * «3 أيلول 2026، 10:00» in Damascus time, Latin digits, Levantine months.
 * Used for delivery promises, where the reader's own clock is irrelevant.
 */
export function arabicDateTimeDamascus(value?: string | Date | null): string {
  if (!value) return "";
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Damascus",
    day: "numeric",
    month: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(d);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? "";
  const month = LEVANT_MONTHS[Number(get("month")) - 1] ?? "";
  return `${Number(get("day"))} ${month} ${get("year")}، ${get("hour")}:${get("minute")}`;
}

/**
 * Arabic plural agreement for minutes:
 * 1 → دقيقة واحدة، 2 → دقيقتان، 3–10 → 5 دقائق، 11+ → 17 دقيقة.
 */
export function arabicMinutes(value?: number | null): string {
  const n = Math.max(1, Math.round(value ?? 5));
  if (n === 1) return "دقيقة واحدة";
  if (n === 2) return "دقيقتان";
  if (n <= 10) return `${n} دقائق`;
  return `${n} دقيقة`;
}

/** «17 دقيقة قراءة» */
export function arabicReadingTime(value?: number | null): string {
  return `${arabicMinutes(value)} قراءة`;
}
